import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CartService } from 'app/core/cart/cart.service';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

export interface EOPackage {
    id: string;
    tag: string;
    name: string;
    badge: string;
    badgeColor: string;
    price: number;
    priceFormatted: string;
    originalPrice?: number;
    originalPriceFormatted?: string;
    image: string;
    description: string;
    features: string[];
    deliverables: string[];
}

interface ManpowerRole {
    id: string;
    name: string;
    description: string;
    image: string;
}

@Component({
    selector: 'app-event-organizer',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './event-organizer.component.html',
    styleUrls: ['./event-organizer.component.scss'],
})
export class EventOrganizerComponent implements OnInit {
    whatsappNumber = '6282260084847';

    manpowerRoles: ManpowerRole[] = [];
    selectedManpowerRoleId = '';

    packages: EOPackage[] = [
        {
            id: 'eo_integrated',
            tag: 'FULL SERVICE',
            name: 'Integrated Event Organizer',
            badge: 'Paling Lengkap',
            badgeColor: 'bg-gradient-to-r from-teal-500 to-emerald-500',
            price: 15000000,
            priceFormatted: 'Rp 15.000.000',
            originalPrice: 20000000,
            originalPriceFormatted: 'Rp 20.000.000',
            image: '/assets/visidea/eo-integrated.jpg',
            description: 'Paket lengkap penyelenggaraan event dari konsep, perencanaan, produksi, hingga eksekusi dengan sistem terintegrasi dan dukungan teknologi digital.',
            features: [
                'Proposal otomatis sesuai kebutuhan event',
                'Booking & payment terintegrasi',
                'Monitoring progres real-time',
                'Manpower dan tim profesional berpengalaman',
                'Photobooth & Videobooth untuk event',
                'Smart ticketing & registrasi tamu'
            ],
            deliverables: [
                'Master Concept, Theme Development & Rundown (Run of Show)',
                'Vendor & Production Management (Stage, Sound, Lighting, LED)',
                'Smart Ticketing & QR Gate System Access Control',
                'Photobooth & 360 Videobooth Installation & Unlimited Print/Share',
                'Professional Photo & Cinematic Video Documentation',
                'Real-time Progress Dashboard & Comprehensive Post-Event Analytics'
            ]
        },
        {
            id: 'eo_custom',
            tag: 'SPECIAL EVENT',
            name: 'Custom Event',
            badge: 'Opsi Sesuai Kebutuhan',
            badgeColor: 'bg-gradient-to-r from-teal-500 to-emerald-500',
            price: 5000000,
            priceFormatted: 'Rp 5.000.000',
            image: '/assets/visidea/eo-custom.jpg',
            description: 'Wujudkan acara impian Anda dengan konsep yang unik dan disesuaikan sepenuhnya dengan kebutuhan, tema, dan anggaran Anda.',
            features: [
                'Custom konsep dan tema acara',
                'Fleksibel sesuai kebutuhan dan anggaran',
                'Cocok untuk wedding, birthday, corporate, komunitas',
                'Konsultasi dengan tim kreatif',
                'Dukungan vendor terpercaya',
                'Request quotation sesuai kebutuhan'
            ],
            deliverables: [
                'Sesi Brainstorming & Moodboard Visual Kustom',
                'Modul Layanan Modular yang Disesuaikan dengan Budget',
                'Koordinasi Vendor Terpilih & Talent Management',
                'Dedicated Event Coordinator & Floor Director',
                'Timeline & Technical Briefing Sebelum Hari-H',
                'Pendampingan Konsultasi Konsep Tanpa Batas'
            ]
        },
        {
            id: 'eo_collaboration',
            tag: 'COLLABORATION',
            name: 'Event Collaboration',
            badge: 'Kolaborasi & Partnership',
            badgeColor: 'bg-gradient-to-r from-teal-500 to-emerald-500',
            price: 3000000,
            priceFormatted: 'Rp 3.000.000',
            image: '/assets/visidea/eo-collaboration.jpg',
            description: 'Bersama membangun acara yang lebih besar dan berdampak melalui kolaborasi dengan brand, komunitas, kampus, atau vendor untuk menciptakan pengalaman event yang luar biasa.',
            features: [
                'Kolaborasi dengan brand & perusahaan',
                'Event bersama komunitas dan organisasi',
                'Program kemitraan untuk kampus',
                'Kerja sama dengan vendor dan talent',
                'Dukungan promosi dan publikasi',
                'Partnership form & proposal khusus'
            ],
            deliverables: [
                'Skema Kemitraan Strategis, Sponsorship, & Co-Production',
                'Publikasi & Cross-Promotion di Ekosistem Digital Visidea',
                'Penyediaan Perangkat Event Tech (Gate, Photobooth, Invitations)',
                'Akses Jaringan Komunitas, Kampus, dan Media Partner',
                'Dukungan Guest Stars, MC, dan Talent Eksternal',
                'Surat Perjanjian Kerja Sama (MOU / Kontrak Resmi)'
            ]
        }
    ];

    selectedPackageForModal: EOPackage | null = null;
    isConsultationModalOpen: boolean = false;

    consultationForm = {
        eventName: '',
        eventDate: '',
        eventLocation: '',
        expectedAttendees: '100 - 500',
        packageInterest: 'Integrated Event Organizer',
        contactName: '',
        contactPhone: '',
        notes: ''
    };

    constructor(
        private cartService: CartService,
        private router: Router,
        private apiService: ApiService
    ) { }

    ngOnInit(): void {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
        void this.fetchManpowerRoles();
    }

    async fetchManpowerRoles(): Promise<void> {
        const manpowerProductId = '01a0d760-3394-71ba-8e03-071c7c2bc166';

        try {
            const [productResponse, packageResponse] = await Promise.all([
                this.apiService.get(
                    `https://dev.api.visideastudio.com/api/v1/front/public/non-themed-products/${manpowerProductId}`,
                    false
                ),
                this.apiService.get(
                    `https://dev.api.visideastudio.com/api/v1/front/public/non-themed-product-packages/list?paginate=false&non_themed_product_id=${manpowerProductId}`,
                    false
                ),
            ]);
            const roles = productResponse?.data?.packages;
            const packageDetails = packageResponse?.data?.list;

            if (!Array.isArray(roles)) {
                return;
            }

            this.manpowerRoles = roles.map((role: { id: string; name: string }) => {
                const packageDetail = Array.isArray(packageDetails)
                    ? packageDetails.find((item: { id: string }) => item.id === role.id)
                    : null;

                return {
                    id: role.id,
                    name: role.name,
                    description: packageDetail?.description || '',
                    image: packageDetail?.image?.original_url || '',
                };
            });
            this.selectedManpowerRoleId = this.manpowerRoles[0]?.id || '';
        } catch (error) {
            console.error('Failed to load Manpower roles', error);
        }
    }

    selectManpowerRole(roleId: string): void {
        this.selectedManpowerRoleId = roleId;
    }

    consultManpower(): void {
        const role = this.manpowerRoles.find(item => item.id === this.selectedManpowerRoleId);
        const roleName = role?.name || 'Manpower';
        const message = `Halo Visidea Studio, saya ingin konsultasi kebutuhan manpower untuk posisi ${roleName}. Mohon informasi ketersediaan dan penawarannya. Terima kasih!`;
        if (typeof window !== 'undefined') {
            window.open(`https://wa.me/${this.whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
        }
    }

    openDetailModal(pkg: EOPackage): void {
        this.selectedPackageForModal = pkg;
    }

    closeDetailModal(): void {
        this.selectedPackageForModal = null;
    }

    addToCart(pkg: EOPackage, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        void this.cartService.startDirectCheckout({
            id: `eo_${pkg.id}`,
            name: `${pkg.name}`,
            category: 'Event Organizer',
            price: pkg.price,
            image: pkg.image,
            description: `${pkg.tag} - ${pkg.description}`,
            selectedOption: `Paket: ${pkg.name}`
        }, 1);

        if (this.selectedPackageForModal) {
            this.closeDetailModal();
        }
    }

    checkout(pkg: EOPackage, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        this.cartService.addItem({
            id: `eo_${pkg.id}`,
            name: `${pkg.name}`,
            category: 'Event Organizer',
            price: pkg.price,
            image: pkg.image,
            description: `${pkg.tag} - ${pkg.description}`,
            selectedOption: `Paket: ${pkg.name}`
        }, 1);

        if (this.selectedPackageForModal) {
            this.closeDetailModal();
        }

    }

    openConsultationModal(pkg?: EOPackage): void {
        if (pkg) {
            this.consultationForm.packageInterest = pkg.name;
        }
        this.isConsultationModalOpen = true;
    }

    closeConsultationModal(): void {
        this.isConsultationModalOpen = false;
    }

    submitConsultation(): void {
        if (!this.consultationForm.eventName || !this.consultationForm.contactPhone) {
            Swal.fire({
                icon: 'warning',
                title: 'Data Belum Lengkap',
                text: 'Harap isi nama event dan nomor WhatsApp Anda.',
                confirmButtonColor: '#102B51'
            });
            return;
        }

        const msg = `Halo Visidea Studio, saya ingin konsultasi layanan Event Organizer *${this.consultationForm.packageInterest}*:%0A%0A` +
            `• *Nama Acara*: ${encodeURIComponent(this.consultationForm.eventName)}%0A` +
            `• *Tanggal Acara*: ${encodeURIComponent(this.consultationForm.eventDate || '-')}%0A` +
            `• *Lokasi / Venue*: ${encodeURIComponent(this.consultationForm.eventLocation || '-')}%0A` +
            `• *Estimasi Pengunjung*: ${encodeURIComponent(this.consultationForm.expectedAttendees)}%0A` +
            `• *Pilihan Paket*: ${encodeURIComponent(this.consultationForm.packageInterest)}%0A` +
            `• *Nama Kontak*: ${encodeURIComponent(this.consultationForm.contactName || '-')}%0A` +
            `• *No. WhatsApp*: ${encodeURIComponent(this.consultationForm.contactPhone)}%0A` +
            `• *Catatan Tambahan*: ${encodeURIComponent(this.consultationForm.notes || '-')}%0A%0A` +
            `Mohon dibantu proposal teknis dan penawaran harganya. Terima kasih!`;

        const waUrl = `https://wa.me/${this.whatsappNumber}?text=${msg}`;
        this.closeConsultationModal();

        if (typeof window !== 'undefined') {
            window.open(waUrl, '_blank');
        }
    }

    chatWhatsApp(pkg?: EOPackage): void {
        const packageName = pkg ? ` mengenai *${pkg.name}*` : '';
        const msg = `Halo Visidea Studio, saya tertarik dengan layanan Event Organizer (EO)${packageName}. Bisa dibantu informasi detail dan diskusinya? Terima kasih!`;
        const waUrl = `https://wa.me/${this.whatsappNumber}?text=${encodeURIComponent(msg)}`;
        if (typeof window !== 'undefined') {
            window.open(waUrl, '_blank');
        }
    }
}
